import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, TrendingUp } from 'lucide-react';
import { fireConfettiCelebration } from '../../utils/celebration';

const tierGradient = {
  Beginner: 'from-gray-500 to-gray-600',
  Active: 'from-blue-500 to-blue-600',
  Advanced: 'from-violet-500 to-indigo-600',
  Elite: 'from-indigo-500 to-purple-600',
  'Top Emperor': 'from-yellow-400 via-amber-500 to-orange-500',
};

/**
 * Acknowledges a credibility tier increase (Beginner -> Active -> Advanced
 * -> Elite -> Top Emperor). Tier changes were otherwise completely silent
 * -- the score/tier just updated on next render with no acknowledgment at
 * all, the same gap BadgeEarnedToast closes for badges.
 */
const TierUpToast = ({ tier, onDismiss }) => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const showTimer = requestAnimationFrame(() => {
      setVisible(true);
      fireConfettiCelebration();
    });
    const hideTimer = setTimeout(() => setVisible(false), 6500);
    const removeTimer = setTimeout(onDismiss, 6900);
    return () => {
      cancelAnimationFrame(showTimer);
      clearTimeout(hideTimer);
      clearTimeout(removeTimer);
    };
  }, [onDismiss]);

  if (!tier) return null;

  const gradient = tierGradient[tier] || tierGradient.Beginner;

  return (
    <div
      // top-40, not top-20 -- BadgeEarnedToast renders at top-20, and both
      // can legitimately fire on the same page load (a badge earned and a
      // tier-up detected in the same visit). Stacked, not overlapping.
      className={`fixed top-40 right-4 z-[100] w-[min(22rem,calc(100vw-2rem))] transition-all duration-300 ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-3 pointer-events-none'
      }`}
      role="status"
      aria-live="polite"
    >
      <div className={`relative overflow-hidden rounded-2xl border-2 border-transparent bg-gradient-to-br ${gradient} shadow-xl p-4`}>
        <button
          onClick={() => setVisible(false)}
          className="absolute top-2 right-2 text-white/70 hover:text-white"
          aria-label={t('profileWidgets.tierUpToast.dismissAria')}
        >
          <X className="w-4 h-4" />
        </button>
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-white/20 backdrop-blur-sm flex-shrink-0">
            <TrendingUp className="w-9 h-9 text-white" />
          </div>
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-wide text-white/80 font-semibold">{t('profileWidgets.tierUpToast.tierUpLabel')}</p>
            <p className="text-sm font-bold text-white truncate">{t('profileWidgets.tierUpToast.nowTier', { tier })}</p>
            <p className="text-xs text-white/80">{t('profileWidgets.tierUpToast.trackRecordPayingOff')}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TierUpToast;
